<script setup lang="ts">
// Single-scheme quote display (back office): binds to ProductOutput.ReturnSchemes[] with Covers[]; edits mutate the model and queue a single re-quote (DoQuote, which re-rates and saves), honored by BuildCoverInputs.
import { computed, ref, watch, onUnmounted, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRiskStore } from '@/stores/riskData';
import { quoteBlocked } from '@/composables/quoteDisplaySignal';
import { DoQuote, UpdateQuote, SaveQuoteStage, EmailQuote } from '@/composables/useQuoteAPIRequests';
import { anySelectedCoverDeclined, anySelectedMandatoryCoverDeclined, isCoverDeclined } from '@/utils/coverDecline';
import { resolveDeclineLead } from '@/components/Quote/config/productRegistry';
import Modal from '@/components/Common/Modal.vue';
import SummaryBar from './SummaryBar.vue';
import QuoteSummary from './QuoteSummary.vue';
import PdfIcon from '@/components/Common/PdfIcon.vue';
import CoverCard from './CoverCard.vue';
import ReviewedAnswers from '@/components/Quote/QuoteDetailsDisplay/ReviewedAnswers.vue';
import NoPaymentNeeded from './NoPaymentNeeded.vue';
import EndorsementModal from './EndorsementModal.vue';
import PolicyEndorsements from './PolicyEndorsements.vue';
import { useSingleSchemeSummary } from './useSingleSchemeSummary';
import { useReviewMode } from './useReviewMode';
import { useOptimisticPremium, type OptimisticPrediction } from './useOptimisticPremium';
import { isCustomerSite } from '@/formEnvironment';

defineOptions({ name: 'QuoteTabSingleScheme' });

const props = defineProps<{ productId: string; action: string; quoteId: string; mode?: 'quote' | 'confirmed' | 'review' }>();
const emit = defineEmits<{ (e: 'nav-back'): void; (e: 'nav-next'): void; (e: 'enter-review'): void }>();

const store = useRiskStore();
const { productOutput, riskData } = storeToRefs(store);
const agentId = (window as any).agentId;
const ownerId = (window as any).modelData ? (window as any).modelData.OwnerId : null;

// Confirmed quotation: same display as the quote, but with a policy start / renewal header, only the selected covers, and a Buy now action.
const isConfirmed = computed(() => props.mode === 'confirmed');
const isReviewMode = computed(() => props.mode === 'review');
// Reviewed/released referral (server-seeded): show the priced quote (not the review card) and lock cover edits.
const quoteReviewed = (window as any).isReviewed === true;

// BuildCoverInputs now preserves incoming cover edits, so the re-quote round-trip is live:
// each cover/subcover toggle or limit change queues a save + re-quote.
const SERVER_REQUOTE_ENABLED = true;

const { scheme, price, priceDisplay, isMonthly, coverMonthly, setMonthly, quoteRef, total, monthly, plan, documents, docName } = useSingleSchemeSummary(props.quoteId);
const dockDocsOpen = ref(false);
const byOrder = (a: any, b: any) => (a?.DisplayOrder ?? 0) - (b?.DisplayOrder ?? 0);
const allCovers = computed<any[]>(() => [...(scheme.value?.Covers ?? [])].sort(byOrder));
// Confirmed shows only the covers already taken. Once a cover has appeared here (mandatory, selected
// on entry, or re-selected since) keep it on the page even after the user deselects it — so a
// deselected optional cover stays visible (unticked, re-selectable) instead of vanishing immediately.
const shownCoverIds = ref<Set<number>>(new Set());
watch(allCovers, (list) => {
    if (!isConfirmed.value && !isReviewMode.value) return;
    for (const c of list) if (c.IsMandatory || c.IsSelected) shownCoverIds.value.add(c.CoverId);
}, { immediate: true });
// Headline covers shown above the "consider" heading (per Figma): the mandatory covers (Property /
// Combined / PL) plus Employers' Liability. Optional add-ons (Cyber, Legal, OPA…) drop below.
const isEmployersLiability = (c: any) => c.CoverId === 2 || /employ/i.test(c.CoverName ?? '');
const isPublicLiability = (c: any) => /public/i.test(c.CoverName ?? '') && /liab/i.test(c.CoverName ?? '');
const isLegalExpenses = (c: any) => /legal/i.test(c.CoverName ?? '');
const isConsiderCover = (c: any) => !c.IsMandatory && !isEmployersLiability(c) && !isPublicLiability(c);
// Customersite confirmed display always offers Legal Expenses, even unticked (Figma), with a prompt.
const showLegalPromo = (c: any) => isConfirmed.value && isCustomerSite && isLegalExpenses(c) && !c.IsSelected && !quoteReviewed;
const covers = computed<any[]>(() => {
    if (isConfirmed.value || isReviewMode.value)
        return allCovers.value.filter((c: any) =>
            c.IsMandatory || shownCoverIds.value.has(c.CoverId) || (isConfirmed.value && isCustomerSite && isLegalExpenses(c) && !quoteReviewed));
    return [...allCovers.value.filter((c: any) => !isConsiderCover(c)), ...allCovers.value.filter(isConsiderCover)];
});
// "consider" heading is inserted before the first consider cover (quote mode only).
const firstConsiderId = computed(() =>
    (isConfirmed.value || isReviewMode.value) ? null : covers.value.find(isConsiderCover)?.CoverId ?? null);
const isBuildingOwned = computed(() => riskData.value?.IsBuildingOwned);
const schemeIptRate = computed(() => scheme.value?.QuotedPremium?.IptRate ?? 0);
const schemeReviewId = computed(() => scheme.value?.SchemeId);

// Underwriter review: premium override + endorsement persistence, decline, finalise (endorsement fetch is review-only).
const {
    isBusy: reviewBusy, persistCover, filteredEndos, policyEndorsements,
    addOpen, openAddEndorsement, onEndoSubmit, removeCoverEndorsement, removePolicyEndorsement,
    declineOpen, declineReason, submitDecline,
    finaliseOpen, reviewComments, submitFinalise,
} = useReviewMode({ productId: props.productId, action: props.action, quoteId: props.quoteId, isReview: isReviewMode, scheme, schemeId: schemeReviewId });

// ---- confirmed: policy start date (within 30 days) + auto-renew ----
const toISODate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = toISODate(new Date());
const maxIso = toISODate(new Date(Date.now() + 30 * 864e5));
// MTA effective-date floor (server-computed): today, or the closest existing future-dated change.
const mtaMinIso = ((window as any).mtaEffectiveDateMin as string) || todayIso;
const initStartDate = () => {
    // MTA: default the effective date to the earliest valid date (today, or the closest existing change).
    if (/mta/i.test(props.action ?? '')) return mtaMinIso;
    const s = riskData.value?.StartDateString;
    if (typeof s === 'string' && s.includes('/')) { const [d, m, y] = s.split('/'); return `${y}-${m}-${d}`; }
    const iso = riskData.value?.StartDate;
    return iso ? String(iso).slice(0, 10) : todayIso;
};
const startDate = ref(initStartDate());
const startDateError = ref('');

// NB: the start date doesn't change the premium, so each change isn't re-rated; Buy now re-rates once so the
// server derives the start-aligned end date. MTA: the effective date drives the pro-rata premium, so re-rate on change.
const isMta = computed(() => (props.action ?? '').toLowerCase().includes('mta'));
// Bottom-dock (narrow screens) headline figure: monthly instalment on the monthly plan, else the total.
const bottomDockAmount = computed(() => (isMonthly.value && monthly.value ? monthly.value.amount : total.value));
const bottomDockLabel = computed(() => isMonthly.value ? 'Monthly' : (isMta.value ? (total.value < 0 ? 'Refund due' : 'Adjustment') : 'Total'));
// Date-picker bounds: new business = next 30 days; MTA = from the effective-date floor, 30-day window.
const effMinIso = computed(() => isMta.value ? mtaMinIso : todayIso);
const effMaxIso = computed(() => isMta.value ? toISODate(new Date(new Date(mtaMinIso).getTime() + 30 * 864e5)) : maxIso);
// On load, pull a retrieved new-business start date that has fallen into the past up to today (and a
// too-far-future one back to the max)
onMounted(() => {
    if (isMta.value || !startDate.value) return;
    if (startDate.value < effMinIso.value) startDate.value = effMinIso.value;
    else if (startDate.value > effMaxIso.value) startDate.value = effMaxIso.value;
});



// Purchase-time start date guard
function startWindowValid(): boolean {
    const v = startDate.value;
    const ok = !!v && v >= effMinIso.value && v <= effMaxIso.value;
    startDateError.value = ok ? '' : (isMta.value
        ? 'Please choose an amendment effective date within the allowed window.'
        : 'Please choose a policy start date from today onwards (within the next 30 days).');
    if (!ok) {
        const el = document.querySelector('.ssq-date') as HTMLElement | null;
        if (el) {
            const top = el.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 100;
            try { window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' }); } catch { window.scrollTo(0, Math.max(top, 0)); }
        }
    }
    return ok;
}

function onStartDateChange() {
    if (!startDate.value) return;
    // Clamp to the valid window for native mobile date pickers (iOS/Android)
    if (startDate.value < effMinIso.value) startDate.value = effMinIso.value;
    else if (startDate.value > effMaxIso.value) startDate.value = effMaxIso.value;
    startDateError.value = '';   // now within the window

    if (!isMta.value) return;
    // MTA: only the effective date moves (inception/StartDate stays); the server re-rates the pro-rata.
    const [y, m, d] = startDate.value.split('-');
    const iso = `${startDate.value}T00:00:00.000Z`;
    store.setFormDataSection('riskData', {
        ...(riskData.value ?? {}), EffectiveDate: iso, EffectiveDateString: `${d}/${m}/${y}`,
    });
    queueRequote({ coverId: -1, kind: 'select' });
}

// Fold the start/effective date into a fresh risk copy and persist it (policies always renew automatically).
function prepareRiskForPurchase() {
    const risk = JSON.parse(JSON.stringify(riskData.value ?? {}));
    if (startDate.value) {
        const [y, m, d] = startDate.value.split('-');
        const iso = `${startDate.value}T00:00:00.000Z`;
        const disp = `${d}/${m}/${y}`;
        // MTA: the chosen date is the amendment effective date; new business: the policy start date.
        if (isMta.value) { risk.EffectiveDate = iso; risk.EffectiveDateString = disp; }
        else { risk.StartDate = iso; risk.StartDateString = disp; }
    }
    risk.auto_renew = true; // no customer toggle — policies always bind as auto-renewing (per NB use cases)
    store.setFormDataSection('riskData', risk);
    return risk;
}

// Full-page POST that hands the SPA off to an MVC purchase action, carrying the anti-forgery token.
function postToMvc(action: string, fields: Record<string, unknown>) {
    const token = (document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement | null)?.value;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    const allFields = { ...fields, ...(token ? { __RequestVerificationToken: token } : {}) };
    for (const [name, value] of Object.entries(allFields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
}

// £0/refund MTA takes no card payment; additions still go via Stripe.
const isNoPaymentMta = computed(() =>
    isMta.value && Number(scheme.value?.ProRataPremium?.TotalPayable ?? 0) <= 0);
const noPaymentOpen = ref(false);
const noPaymentAmount = computed(() => Number(scheme.value?.ProRataPremium?.TotalPayable ?? 0));
const noPaymentInstalment = computed(() => Number(scheme.value?.ProRataPaymentPlan?.InstallmentAmount ?? 0));
const noPaymentPrevInstalment = computed(() => Number(scheme.value?.ProRataPaymentPlan?.PreviousInstallmentAmount ?? 0));

async function buyNow() {
    if (anySelectedDeclined.value || isRequoting.value) return;
    if (!startWindowValid()) return;
    // £0 / refund MTA: no card transaction — confirm, then bind without payment.
    if (isNoPaymentMta.value) { noPaymentOpen.value = true; return; }
    const risk = prepareRiskForPurchase();
    isRequoting.value = true;
    try {
        // Re-rate on Buy now (not a plain partial save) so the server derives the end date from the chosen start
        await fullRequote(risk);
        // POST to Confirmation persists the monthly/annual choice, then routes to Stripe (MTA = pro-rata).
        const amount = Number((isMta.value ? scheme.value?.ProRataPremium : scheme.value?.QuotedPremium)?.TotalPayable ?? 0);
        postToMvc('/retail-shops/confirmation', { isMonthly: isMonthly.value, amount });
    } catch (e) {
        console.error('Buy now failed:', e);
        isRequoting.value = false;
    }
}

// From the no-payment modal: persist, then bind the £0/refund adjustment directly (no Stripe).
async function confirmNoPayment() {
    if (isRequoting.value) return;
    if (!startWindowValid()) { noPaymentOpen.value = false; return; }
    const risk = prepareRiskForPurchase();
    isRequoting.value = true;
    try {
        await SaveQuoteStage(props.action, props.productId, props.quoteId, risk, agentId);
        postToMvc('/payment/bindwithouttransaction', { paymentRef: quoteRef.value, paymentMethod: 'Stripe' });
    } catch (e) {
        console.error('Confirm amendment failed:', e);
        isRequoting.value = false;
        noPaymentOpen.value = false;
    }
}

// ---- edits: mutate the model, then queue a re-quote (one call per change) ----
const isRequoting = ref(false);
const isSaving = ref(false);
// A quote update is in flight (full re-quote, optimistic save, or review action) — disable every control.
const busy = computed(() => isRequoting.value || isSaving.value || reviewBusy.value);
// Returning to this step and re-rating the initial-only quote — show the skeleton placeholder meanwhile.
const isRerating = computed(() => store.initialOnlyRerating && !isConfirmed.value && !isReviewMode.value);
// A limit change re-prices that cover, so its card is overlaid too; a selection only moves the total.
const requotingCoverId = ref<number | null>(null);

const optimisticEnabled = ref((window as any).featureFlags?.optimisticRerate === true);
const isOptimistic = (kind: 'limit' | 'select') => optimisticEnabled.value && kind === 'select' && !isMta.value;
const { applyOptimistic, reportDrift } = useOptimisticPremium();

// A re-rate (update) that threw — surfaces the "problem updating" modal; the last change is retried as-is.
const rerateFailed = ref(false);
let lastRequote: { coverId: number; kind: 'limit' | 'select' } | undefined;

const queueRequote = async (detail?: { coverId: number; kind: 'limit' | 'select' }) => {
    if (!SERVER_REQUOTE_ENABLED) return;
    if (detail) lastRequote = detail;
    const coverId = detail?.coverId ?? null;
    const kind = detail?.kind ?? 'select';
    const dateChanged = coverId === -1;

    // Optimistic (NB select toggle): the annual total already reflects the toggle, so flag a background
    // save; a limit change overlays its card while re-rating, anything else is a plain obstructive re-rate.
    let predicted: OptimisticPrediction | null = null;
    if (kind === 'select' && !dateChanged && isOptimistic('select')) {
        isSaving.value = true;
        isRequoting.value = false;
        requotingCoverId.value = null;
        // Compute the new monthly client-side so it updates instantly; the server result reconciles below.
        predicted = applyOptimistic(productOutput.value);
    } else {
        isRequoting.value = true;
        isSaving.value = false;
        requotingCoverId.value = (kind === 'limit' && coverId != null && coverId >= 0) ? coverId : null;
    }

    // Keep the client riskData in sync with the cover model (source of truth for selection/limits).
    const risk = JSON.parse(JSON.stringify(riskData.value ?? {}));
    applyModelToRisk(risk);
    store.setFormDataSection('riskData', risk);
    try {
        const coverModel = (!dateChanged && coverId != null && coverId >= 0)
            ? allCovers.value.find((c: any) => Number(c.CoverId) === coverId) : null;
        if (coverModel) {
            const { UpdateQuote: res } = await UpdateQuote(props.action, props.productId, props.quoteId, {
                schemeId: schemeReviewId.value, page: 2, showReviewLists: false, addonQuoteRequest: false,
                cover: coverModel, selectOnly: kind === 'select', initialOnly: props.mode === 'quote',
            });
            if (res) {
                reportDrift(predicted, res); // validate the optimistic maths before the flag goes live
                store.setPayload({ ...(store.payload ?? {}), productOutput: res, action: props.action ?? '' });
            } else {
                await fullRequote(risk); // empty/!ok response → fall back so the display never stalls
            }
        } else {
            await fullRequote(risk);
        }
    } catch (e) {
        console.error('Re-quote failed:', e);
        rerateFailed.value = true;
    } finally { isRequoting.value = false; isSaving.value = false; requotingCoverId.value = null; }
};

// "Try again" on the error modal re-fires the exact change that failed (same /update or DoQuote call).
const retryRerate = () => { rerateFailed.value = false; void queueRequote(lastRequote); };

// Serialise the shown covers (the source of truth for selection/limits) into the CoverInput tree,
// reusing existing inputs by CoverId so server-set fields survive; the server seeds/enforces defaults.
const applyModelToRisk = (risk: any) => {
    if (!risk || typeof risk !== 'object') return;
    const existing = new Map<number, any>();
    const index = (list: any[]) => { for (const ci of list ?? []) { existing.set(ci.CoverId, ci); index(ci.Subcovers); } };
    index(risk.Covers);
    const toInput = (item: any): any => {
        const ci = existing.get(item.CoverId) ?? { CoverId: item.CoverId };
        ci.IsSelected = !!item.IsSelected;
        ci.Limit = Number(item.Limit) || 0;
        ci.SumInsured = ci.Limit;
        ci.Subcovers = (item.Subcovers ?? []).map(toInput);
        return ci;
    };
    risk.Covers = allCovers.value.map(toInput);
};

// Whole-tree re-rate (DoQuote): re-rates AND persists risk + output. Page 0 = neutral re-rate.
async function fullRequote(risk: any) {
    const { doneQuote } = await DoQuote(props.action, props.productId, props.quoteId, risk, agentId, ownerId, 0, props.mode === 'quote');
    if (doneQuote) store.setPayload({ ...(store.payload ?? {}), productOutput: doneQuote, action: props.action ?? '' });
}

// ---- sidebar / summary UX ----
// The quote display opens on the sidebar by default; docking is an in-view toggle.
const docked = ref(false);

// The sidebar pins with pure CSS (position: sticky) — the customersite chrome has no fixed navbar
// to clip under, and the column layout is simple, so no scroll-driven JS is needed.

// ---- referral / decline / "no quote" handling ----
// Product-level IsReferred/IsDeclined both read true over an empty ReturnSchemes, so use empty ReturnSchemes as the "no quote" signal and read genuine referral/decline off the scheme.
const isReview = computed(() => (props.action ?? '').toLowerCase().includes('review'));
const hasSchemes = computed(() => (productOutput.value?.ReturnSchemes?.length ?? 0) > 0);
const referReasonsOf = (o: any): string[] =>
    [...(o?.ReferReasons ?? []), ...((o?.ReferralOutputs ?? []).map((r: any) => r.SystemDisplay || r.CustomerDisplay))].filter(Boolean);
// Scheme-level reasons plus any referred mandatory cover's reasons, deduped, so the top card lists them all.
const referReasons = computed<string[]>(() => [...new Set([
    ...referReasonsOf(scheme.value),
    ...allCovers.value.filter((c: any) => c.IsMandatory && coverReferred(c)).flatMap((c: any) => referReasonsOf(c)),
])]);
const declineReasons = computed<string[]>(() => {
    const s = scheme.value;
    return [...(s?.DeclineReasons ?? []), ...((s?.DeclineOutputs ?? []).map((d: any) => d.SystemDisplay || d.CustomerDisplay))].filter(Boolean);
});
// A referred mandatory cover blocks continuing — the quote must be reviewed (or left), same as a
// scheme-level referral. Optional covers don't block: the user can deselect them.
const coverReferred = (c: any): boolean => c?.IsReferred === true || referReasonsOf(c).length > 0;
const mandatoryCoverReferred = computed(() => allCovers.value.some((c: any) => c.IsMandatory && coverReferred(c)));

// A selected declined cover declines the whole quote
const takenCoverDeclined = computed(() => isCustomerSite &&
    allCovers.value.some((c: any) => (c?.IsMandatory || c?.IsSelected) && isCoverDeclined(c)));

const schemeDeclined = computed(() => scheme.value?.IsDeclined === true || declineReasons.value.length > 0 || takenCoverDeclined.value);
// Primary shop's DeclineLead decides the decline screen: Ollo (direct to our broker) vs biba (default).
const olloUrl = 'https://quote.olloinsurance.co.uk/ollo/Quote';
const olloLogoUrl = 'https://isistech.blob.core.windows.net/cdn/resources/Mettle/ollo_header_logo_001.png';
const isOlloDecline = computed(() => (resolveDeclineLead(props.productId, riskData.value) ?? '').toLowerCase() === 'ollo');
const schemeReferred = computed(() => scheme.value?.IsReferred === true || referReasons.value.length > 0 || mandatoryCoverReferred.value);
const referralActive = computed(() => schemeReferred.value && !isReview.value && !quoteReviewed);
const schemeBlocked = computed(() => schemeDeclined.value || referralActive.value);

// Transaction-type wording for the blocked-screen copy (MD parity): MTA = amendment, renewal = renewal.
const isRenewal = computed(() => (props.action ?? '').toLowerCase().includes('renewal'));
const referredFrom = computed(() => isMta.value ? 'amendment quotation' : isRenewal.value ? 'renewal quotation' : 'quotation');

// Cover-level decline
const anySelectedDeclined = computed(() => anySelectedCoverDeclined(allCovers.value));
const anySelectedMandatoryDeclined = computed(() => anySelectedMandatoryCoverDeclined(allCovers.value));
const declineHint = computed(() => anySelectedMandatoryDeclined.value
    ? "A required cover has been declined, so this quote can't be purchased."
    : 'Deselect the declined cover to continue.');
const continueDeclineHint = "A required cover has been declined, so we can't continue with this quote.";
// Review is read-only (no deselect control), so a selected declined cover blocks Purchase — the UW declines or finalises without purchasing.
const reviewDeclineHint = "A declined cover is selected, so this quote can't be purchased.";

// Hide the journey page title while blocked — the referral/decline card carries its own heading.
watch(schemeBlocked, (b) => { quoteBlocked.value = b; }, { immediate: true });
onUnmounted(() => { quoteBlocked.value = false; });

// Sidebar primary CTA: mirrors the button below the covers — "Confirm & continue" on a quote,
// "Buy now" once confirmed; hidden in review / referred / declined states.
// CTA: "Confirm amendment" for a no-payment MTA, else "Buy now".
const confirmCtaLabel = computed(() => isNoPaymentMta.value ? 'Confirm amendment →' : 'Buy now →');
const sidebarPrimaryLabel = computed(() => {
    if (isReviewMode.value || schemeBlocked.value || referralActive.value) return '';
    if (isConfirmed.value && anySelectedDeclined.value) return ''; // a selected declined cover blocks purchase
    if (!isConfirmed.value && anySelectedMandatoryDeclined.value) return ''; // a declined mandatory cover blocks continuing
    if (isRequoting.value || isSaving.value) return 'Saving…'; // re-rate in flight — label both the full and optimistic save
    return isConfirmed.value ? confirmCtaLabel.value : 'Confirm & continue →';
});
const onSidebarPrimary = () => { if (isConfirmed.value) buyNow(); else emit('nav-next'); };

// "There was a problem getting your quote" modal — auto-shown when nothing came back.
const problemOpen = ref(false);
watch([hasSchemes, isReview], ([has, review]) => { problemOpen.value = !has && !review; }, { immediate: true });

const leaveToCustomer = () => { window.location.href = '/'; };

// Review journey
const canReview = computed(() => !!(window as any).overrideRefer);
const reviewAction = computed(() => {
    const a = (props.action ?? '').toLowerCase();
    return a.includes('mta') ? 'MTAReview' : a.includes('renewal') ? 'RenewalReview' : 'Review';
});
const goToReview = () => {
    (window as any).showLoader?.();
    window.location.href = `/retail-shops/quote`;
};
const retryQuote = async () => {
    problemOpen.value = false;
    const risk = riskData.value ? JSON.parse(JSON.stringify(riskData.value)) : {};
    isRequoting.value = true;
    try {
        const { doneQuote } = await DoQuote(props.action, props.productId, props.quoteId, risk, agentId, ownerId, 0, props.mode === 'quote');
        if (doneQuote) store.setPayload({ ...(store.payload ?? {}), productOutput: doneQuote, action: props.action ?? '' });
    } finally { isRequoting.value = false; } // if it's still empty, the watch re-opens the modal
};

// One modal serves both "no schemes came back" (getting) and "a re-rate threw" (updating); the latter wins.
const errorVerb = computed(() => (rerateFailed.value ? 'updating' : 'getting'));
const errorOpen = computed(() => rerateFailed.value || problemOpen.value);
const closeError = () => { rerateFailed.value = false; problemOpen.value = false; };
const retryError = () => (rerateFailed.value ? retryRerate() : retryQuote());

// ---- email quote ----
const emailOpen = ref(false);
const emailAddress = ref('');
const emailSending = ref(false);
const emailResult = ref<'' | 'sent' | 'error'>('');
const openEmail = () => {
    emailAddress.value = riskData.value?.InsuredEmailAddress ?? riskData.value?.EmailAddress ?? '';
    emailResult.value = '';
    emailOpen.value = true;
};
async function sendEmail() {
    emailSending.value = true;
    emailResult.value = '';
    try {
        await EmailQuote(emailAddress.value);
        emailResult.value = 'sent';
        setTimeout(() => { emailOpen.value = false; }, 1400);
    } catch {
        emailResult.value = 'error';
    } finally {
        emailSending.value = false;
    }
}
</script>

<template>
    <div class="single-scheme-quote" :class="{ 'is-docked': docked }">
        <!-- Docked summary: a sticky top bar (also shown on Additional Information via Factory) -->
        <SummaryBar v-if="docked" :show-undock="true" :loading="isRequoting" @undock="docked = false" />

        <template v-if="scheme">
            <!-- Skeleton placeholder while the initial-only quote is retrieved on a return to this step.
                 Takes priority so a stale declined/referred view doesn't show behind the loader. -->
            <template v-if="isRerating">
                <div class="ssq-cols ssq-skeleton" aria-hidden="true">
                    <div class="ssq-main">
                        <div v-for="n in 4" :key="n" class="sk-card">
                            <div class="sk-line sk-title"></div>
                            <div class="sk-line sk-sub"></div>
                            <div class="sk-foot"><span class="sk-chip"></span><span class="sk-price"></span></div>
                        </div>
                    </div>
                    <div class="ssq-side">
                        <div class="sk-summary">
                            <div class="sk-line sk-title"></div>
                            <div class="sk-line sk-sub"></div>
                            <!-- Monthly / Annual toggle -->
                            <div class="sk-toggle"><span class="sk-pill"></span><span class="sk-pill"></span></div>
                            <div class="sk-line sk-wide"></div>
                            <div class="sk-line"></div>
                            <div class="sk-line sk-sub"></div>
                            <!-- Confirm & continue / Email quote -->
                            <div class="sk-btn"></div>
                            <div class="sk-btn sk-btn-ghost"></div>
                            <!-- Documents -->
                            <div class="sk-line sk-title sk-docs-h"></div>
                            <div v-for="d in 3" :key="`d${d}`" class="sk-doc"><span class="sk-doc-ic"></span><span class="sk-doc-ln"></span></div>
                        </div>
                    </div>
                </div>
            </template>
            <!-- Referred / declined (customer): a generic Markel Direct message — no underwriter reasons.
                 Both are terminal here (message only, no covers/summary); the agent review flow keeps the
                 reasons + Review route in the review branch below. -->
            <template v-else-if="schemeBlocked">
            <div class="ssq-blocked" :class="{ declined: schemeDeclined && !isOlloDecline }">
                <em v-if="!(schemeDeclined && isOlloDecline)" class="ssq-blocked-icon far" :class="(schemeDeclined && !isOlloDecline) ? 'fa-circle-xmark' : 'fa-circle-exclamation'"></em>
                <!-- Ollo-led decline: direct the customer to our specialist broker Ollo (DeclineLead on the primary shop). -->
                <template v-if="schemeDeclined && isOlloDecline">
                    <h4 class="ssq-blocked-h">Nearly there…</h4>
                    <p class="ssq-blocked-p">Unfortunately, Markel are unable to provide a {{ referredFrom }} based on the information you have provided. However, we've partnered with specialist insurance brokers at Ollo, who may be able to help.</p>
                    <p class="ssq-blocked-p" v-if="quoteRef && quoteRef !== '—'">Please make a note of your quote reference which is <strong>{{ quoteRef }}</strong>.</p>
                    <img class="ssq-ollo-logo" :src="olloLogoUrl" alt="Ollo" />
                    <div class="ssq-refer-actions">
                        <a class="btn pri" :href="olloUrl" target="_blank" rel="noopener">Get a quote from Ollo <em class="far fa-chevron-right ml-1"></em></a>
                    </div>
                </template>
                <template v-else-if="schemeDeclined">
                    <h4 class="ssq-blocked-h">Sorry, we cannot cover you this time.</h4>
                    <p class="ssq-blocked-p">Based on what you've told us, we can't offer a {{ referredFrom }} as we do not offer an insurance policy that would be suitable for your business. This may be due to your business operating in an industry that we do not cover, your business undertaking activities that we do not cover, or your business being too large for us to cover.</p>
                    <p class="ssq-blocked-p">You may wish to contact an insurance broker to assist your search for cover. You can find an insurance broker at <a href="https://www.biba.org.uk" target="_blank" rel="noopener">www.biba.org.uk</a>.</p>
                    <p class="ssq-blocked-p" v-if="quoteRef && quoteRef !== '—'">Please make a note of your quote reference which is <strong>{{ quoteRef }}</strong>.</p>
                </template>
                <template v-else>
                    <h4 class="ssq-blocked-h">Nearly there…</h4>
                    <p class="ssq-blocked-p">Before we can offer your {{ referredFrom }}, one of our underwriters needs to review a few details. We'll be in touch shortly to discuss your cover.</p>
                    <p class="ssq-blocked-p" v-if="quoteRef && quoteRef !== '—'">Please make a note of your quote reference which is <strong>{{ quoteRef }}</strong>.</p>
                </template>
                <div class="ssq-refer-actions">
                    <button type="button" class="btn out" @click="$emit('nav-back')"><em class="far fa-chevron-left mr-1"></em> Back to questions</button>
                    <button v-if="!schemeDeclined && canReview" type="button" class="btn pri" @click="goToReview">Review</button>
                </div>
            </div>
            </template>

            <template v-else>
            <div v-if="quoteReviewed" class="ssq-reviewed-notice" role="status">
                <em class="far fa-circle-check"></em>
                <div>
                    <strong>Good news &mdash; your quote has been reviewed and is now ready to buy.</strong>
                    <span>Select your policy start date and confirm your cover below to continue. To change your cover or details, please call us on 0800 640 6600.</span>
                </div>
            </div>
            <div v-if="isReviewMode && referReasons.length" class="ssq-refer">
                <h4><em class="far fa-triangle-exclamation mr-2"></em> Refer reasons</h4>
                <ul class="ssq-refer-reasons"><li v-for="(reason, i) in referReasons" :key="i">{{ reason }}</li></ul>
            </div>
            <div class="ssq-cols">
                <!-- LEFT: covers (inside the off-white form container) -->
                <div class="ssq-main">
                    <p class="ssq-intro" v-if="isConfirmed && isMta">
                        Review your amended cover below. You'll pay the pro-rata cost of the change for the remainder of your policy term (or receive a pro-rata refund).
                    </p>
                    <p class="ssq-intro" v-else-if="isConfirmed && !quoteReviewed">
                        Here is the confirmed quotation. Select a policy start date and finalise cover choices before proceeding to buy.
                    </p>
                    <p class="ssq-intro" v-else-if="!quoteReviewed && !isReviewMode">
                        Based on the answers provided, businesses like this consider the cover shown below.
                        Select or deselect optional covers and adjust limits — the price updates dynamically.
                    </p>
                    <div v-if="isConfirmed || isReviewMode" class="ssq-confirm-head">
                        <!-- Start date in a grey fieldset (mirrors the About You panels). Auto-renewal is always on
                             (no customer toggle), so the policy binds as auto-renewing. -->
                        <div class="ssq-cfieldset">
                            <div class="ssq-field">
                                <label>{{ isMta ? 'Amendment effective date' : 'Policy start date' }} <span v-if="!isReviewMode" class="req">*</span></label>
                                <input v-if="!isReviewMode" type="date" class="ssq-date" :class="{ 'is-error': startDateError }" v-model="startDate" :min="effMinIso" :max="effMaxIso" :disabled="busy" @change="onStartDateChange" />
                                <div v-else class="ssq-readonly">{{ (isMta ? riskData?.EffectiveDateString : riskData?.StartDateString) || '—' }}</div>
                                <div v-if="!isReviewMode && !isMta" class="ssq-hint">Must be within the next 30 days.</div>
                                <div v-if="startDateError" class="ssq-date-error" role="alert">{{ startDateError }}</div>
                            </div>
                        </div>
                    </div>
                    <template v-for="cover in covers" :key="cover.CoverId">
                        <div v-if="cover.CoverId === firstConsiderId" class="consider">You may also wish to consider the following cover:</div>
                        <p v-if="showLegalPromo(cover)" class="ssq-legal-promo">Do you want to protect your business against legal costs involved with contract disputes, tax enquiries, recovering debts and more? Add Legal Expenses Insurance to your policy.</p>
                        <CoverCard :cover="cover" :price="priceDisplay" :is-monthly="coverMonthly" :is-mta="isMta" :review="isReviewMode" :reviewed="quoteReviewed" :is-building-owned="isBuildingOwned" :scheme-ipt-rate="schemeIptRate" :loading="isRequoting && requotingCoverId === cover.CoverId" :busy="busy" @change="queueRequote" @override="persistCover" @add-endorsement="openAddEndorsement" @remove-endorsement="removeCoverEndorsement" />
                    </template>

                    <!-- Second summary (normal width): a compact premium recap under the covers, above the
                         buttons (MD). No header / reference / toggle — just the premium + IPT note. -->
                    <QuoteSummary v-if="!isReviewMode" class="ssq-main-summary" compact />

                    <!-- Reviewed retrieval: read-only read-back of every answer, above the buy action (MD's ReviewDisplay). -->
                    <ReviewedAnswers v-if="quoteReviewed && isConfirmed" :product-id="productId" :risk-data="riskData" />

                    <div class="ssq-actions">
                        <div class="act-main">
                            <template v-if="isReviewMode">
                                <span v-if="anySelectedDeclined" class="ssq-decline-hint"><em class="far fa-ban mr-1"></em> {{ reviewDeclineHint }}</span>
                                <button type="button" class="btn danger" :disabled="busy" @click="declineOpen = true"><em class="far fa-ban mr-1"></em> Decline</button>
                                <button type="button" class="btn pri" :disabled="busy" @click="finaliseOpen = true">Finalise review →</button>
                            </template>
                            <template v-else-if="referralActive">
                                <button v-if="canReview" type="button" class="btn pri" :disabled="busy" @click="goToReview">Review</button>
                            </template>
                            <template v-else-if="isConfirmed">
                                <span v-if="anySelectedDeclined" class="ssq-decline-hint"><em class="far fa-ban mr-1"></em> {{ declineHint }}</span>
                                <button type="button" class="btn pri" :disabled="busy || anySelectedDeclined" @click="buyNow">{{ (isRequoting || isSaving) ? 'Saving…' : confirmCtaLabel }}</button>
                            </template>
                            <template v-else>
                                <button v-if="!anySelectedDeclined && !isMta" type="button" class="btn out" :disabled="busy" @click="openEmail">Email quote</button>
                                <span v-if="anySelectedMandatoryDeclined" class="ssq-decline-hint"><em class="far fa-ban mr-1"></em> {{ continueDeclineHint }}</span>
                                <button type="button" class="btn pri" :disabled="busy || anySelectedMandatoryDeclined" @click="$emit('nav-next')">{{ (isRequoting || isSaving) ? 'Saving…' : 'Confirm &amp; continue →' }}</button>
                            </template>
                        </div>
                        <p v-if="!isReviewMode && !isConfirmed && !schemeReferred" class="ssq-act-note">
                            You will be able to review all details before paying. Your quotation is valid for 30 days.
                        </p>
                        <div class="act-back" v-if="!quoteReviewed">
                            <button type="button" class="btn out" :disabled="busy" @click="$emit('nav-back')"><em class="far fa-chevron-left mr-1"></em> Back</button>
                        </div>
                    </div>
                </div>

                <!-- RIGHT: sticky summary card (Documents + Why insure live inside it; hidden when docked) -->
                <div class="ssq-side" v-if="!docked">
                    <div class="summary">
                        <div class="ssq-summary-box">
                            <QuoteSummary title="Payment options" :primary-label="sidebarPrimaryLabel" :primary-disabled="busy" :show-email="!isConfirmed && !anySelectedDeclined && !isMta" @dock="docked = true" @primary="onSidebarPrimary" @email="openEmail" />
                            <transition name="ssq-fade">
                                <div v-if="isRequoting || reviewBusy" class="ssq-loading">
                                    <span class="ssq-spin"></span>
                                    <span class="ssq-loading-txt">Updating</span>
                                </div>
                            </transition>
                        </div>
                        <PolicyEndorsements v-if="isReviewMode" :endorsements="policyEndorsements" @add="openAddEndorsement" @remove="removePolicyEndorsement" />
                    </div>
                </div>
            </div>

            <!-- Narrow screens: the sidebar summary docks to the bottom (COH/TD/MD) — payment toggle, headline
                 figure (incl. IPT), deposit when monthly, the same Email quote + primary action as the main
                 card, and a Documents toggle (the sidebar card that holds them is hidden at this width). -->
            <div v-if="!isReviewMode && sidebarPrimaryLabel && !isRerating" class="ssq-bottom-dock">
                <button v-if="documents.length" type="button" class="bd-docs-btn" :class="{ on: dockDocsOpen }" @click="dockDocsOpen = !dockDocsOpen">
                    <em class="far fa-file-lines"></em> <span>Documents</span>
                    <em class="far bd-docs-chev fa-chevron-down"></em>
                </button>
                <ul v-if="dockDocsOpen && documents.length" class="sel-docs bd-docs-list">
                    <li v-for="(doc, i) in documents" :key="i"><PdfIcon /><a :href="doc.link" target="_blank" rel="noopener">{{ docName(doc.name) }}</a></li>
                </ul>
                <div class="bd-toggle" v-if="monthly && !isMta">
                    <button type="button" class="pt-btn" :class="{ on: isMonthly }" :disabled="busy" @click="setMonthly(true)">Monthly</button>
                    <button type="button" class="pt-btn" :class="{ on: !isMonthly }" :disabled="busy" @click="setMonthly(false)">Annual</button>
                </div>
                <div class="bd-row">
                    <div class="bd-total">
                        <small>{{ bottomDockLabel }}</small>
                        <b>{{ price(Math.abs(bottomDockAmount)) }} <span class="bd-incl">incl. IPT</span></b>
                        <span v-if="isMonthly && plan && plan.deposit > 0" class="bd-dep">Deposit {{ price(plan.deposit) }}</span>
                    </div>
                    <div class="bd-actions">
                        <button v-if="!isConfirmed && !anySelectedDeclined && !isMta" type="button" class="btn out" :disabled="busy" @click="openEmail">Email quote</button>
                        <button type="button" class="btn pri" :disabled="busy || (isConfirmed && anySelectedDeclined)" @click="onSidebarPrimary">{{ sidebarPrimaryLabel }}</button>
                    </div>
                </div>
            </div>
            </template>
        </template>

        <div v-else class="ssq-empty-wrap">
            <div class="ssq-empty">There was a problem getting the quote.</div>
            <div class="ssq-actions">
                <button type="button" class="btn out" @click="$emit('nav-back')"><em class="far fa-chevron-left mr-1"></em> Back</button>
            </div>
        </div>

        <!-- "Problem getting your quote" (no schemes) or "problem updating" (re-rate failed) — Leave / Try again. -->
        <Modal :modelValue="errorOpen" modalName="quoteProblemModal" :tempOperatorScriptVue="true" @close="closeError">
            <template #header><h5 class="modal-title"><em class="far fa-triangle-exclamation mr-2"></em> There was a problem {{ errorVerb }} your quote</h5></template>
            <template #body>
                <p class="mb-0">There was a problem {{ errorVerb }} your quote. This is usually temporary, so please try again. If the problem continues, call us on 0800 640 6600 and we'll be happy to help.</p>
            </template>
            <template #footer>
                <button type="button" class="btn btn-ghost" @click="leaveToCustomer"><em class="far fa-chevron-left mr-1"></em> Leave</button>
                <button type="button" class="btn btn-primary" :disabled="busy" @click="retryError">{{ busy ? 'Trying…' : 'Try again' }}</button>
            </template>
        </Modal>

        <!-- Email quote modal -->
        <Modal :modelValue="emailOpen" modalName="emailQuoteModal" :tempOperatorScriptVue="true" @close="emailOpen = false">
            <template #header><h5 class="modal-title">Email this quote</h5></template>
            <template #body>
                <p class="mb-2">Send a copy of quotation <b>{{ quoteRef }}</b> to:</p>
                <input class="form-control" type="email" v-model="emailAddress" placeholder="name@example.com" :disabled="emailSending" />
                <p v-if="emailResult === 'sent'" class="text-success small mt-2">Quote email sent successfully!</p>
                <p v-if="emailResult === 'error'" class="text-danger small mt-2">Couldn't send the email. Please try again.</p>
            </template>
            <template #footer>
                <button type="button" class="btn btn-ghost" @click="emailOpen = false">Cancel</button>
                <button type="button" class="btn btn-primary" :disabled="emailSending || !emailAddress" @click="sendEmail">
                    {{ emailSending ? 'Sending…' : 'Send' }}
                </button>
            </template>
        </Modal>

        <!-- No-payment MTA (£0 / refund): confirm, then bind without a card transaction. -->
        <NoPaymentNeeded :modelValue="noPaymentOpen" :quote-ref="quoteRef" :amount="noPaymentAmount" :is-monthly="isMonthly" :instalment="noPaymentInstalment" :prev-instalment="noPaymentPrevInstalment" :busy="isRequoting" @confirm="confirmNoPayment" @close="noPaymentOpen = false" />

        <!-- Review: add-endorsement, decline, finalise -->
        <EndorsementModal v-if="isReviewMode" :modelValue="addOpen" :endorsements="filteredEndos" @submit="onEndoSubmit" @close="addOpen = false" />
        <Modal v-if="isReviewMode" :modelValue="declineOpen" modalName="reviewDeclineModal" :tempOperatorScriptVue="true" @close="declineOpen = false">
            <template #header><h5 class="modal-title">Decline</h5></template>
            <template #body>
                <label class="ssq-modal-lbl">Decline reasons</label>
                <textarea class="form-control" v-model="declineReason" rows="3" placeholder="Reason for declining this risk…"></textarea>
            </template>
            <template #footer>
                <button type="button" class="btn btn-outline-secondary" @click="declineOpen = false">Close</button>
                <button type="button" class="btn btn-outline-primary" :disabled="!declineReason" @click="submitDecline">Submit</button>
            </template>
        </Modal>
        <Modal v-if="isReviewMode" :modelValue="finaliseOpen" modalName="reviewFinaliseModal" :tempOperatorScriptVue="true" @close="finaliseOpen = false">
            <template #header><h5 class="modal-title">Finalise Review</h5></template>
            <template #body>
                <label class="ssq-modal-lbl">Review comments (added as a note)</label>
                <textarea class="form-control" v-model="reviewComments" rows="3" placeholder="Underwriter comments…"></textarea>
            </template>
            <template #footer>
                <span v-if="anySelectedDeclined" class="ssq-decline-hint mr-auto"><em class="far fa-ban mr-1"></em> {{ reviewDeclineHint }}</span>
                <button type="button" class="btn btn-outline-primary" :disabled="anySelectedDeclined" @click="anySelectedDeclined ? null : submitFinalise(true)">Purchase</button>
                <button type="button" class="btn btn-outline-secondary" @click="finaliseOpen = false">Close</button>
                <button type="button" class="btn btn-outline-primary" @click="submitFinalise(false)">Finalise Review</button>
            </template>
        </Modal>
    </div>
</template>
